import type { Result } from "@pdfking/errors"
import { Failure, Success } from "@pdfking/errors"

import type { JWTHeader } from "#public"

import { StringToUInt8 } from "../bytes/StringToUInt8"
import { UInt8ToString } from "../bytes/UInt8ToString"
import { Base64URLDecode } from "../encoding/Base64URLDecode"
import { GUIDToUint8 } from "../encoding/GUIDToUint8"
import { JSONDecode } from "../encoding/JSONDecode"
import { HMACSha256 } from "../hashing/HMACSha256"
import { UnixTime } from "../time/UnixTime"

/**
 * Verifies and decodes JWT token using HMAC-SHA256.
 *
 * Validates signature, expiration (exp), and issued-at (iat) claims.
 * Throws on invalid signature, expired, or future tokens.
 *
 * @param token - JWT string to decode
 * @param secret - Secret key for verification
 * @returns Promise resolving to decoded payload (without iat/exp)
 * @throws Error if signature invalid, token expired, or issued in future
 *
 * @example
 * ```ts
 * import { DecodeJWT } from "@pdfking/tfunc"
 *
 * const { value } = DecodeJWT<{ userId: number }>(token, "secret")
 * console.log(payload.userId) // 123
 * ```
 */
// Compare two Uint8Arrays for equality
const compareUint8Arrays = (a: Uint8Array, b: Uint8Array): boolean => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

export const DecodeJWT = <T>(token: string, secret: string): Result<T> => {
  const parts = token.split(".")
  if (parts.length !== 3) return Failure("Invalid JWT format")

  const [encodedHeader, encodedPayload, encodedSignature] = parts

  const headerBytes = Base64URLDecode(encodedHeader)
  const payloadBytes = Base64URLDecode(encodedPayload)
  const providedSignature = Base64URLDecode(encodedSignature)

  const { alg } = JSONDecode(UInt8ToString(headerBytes)) as JWTHeader

  if (alg !== "HS256") return Failure("The server only supports HS256")

  const expectedSignature = GUIDToUint8(
    HMACSha256(StringToUInt8(secret), StringToUInt8(`${encodedHeader}.${encodedPayload}`))
  )

  if (!compareUint8Arrays(providedSignature, expectedSignature))
    return Failure("Invalid JWT signature")

  const { exp, iat, ...payload } = JSONDecode(UInt8ToString(payloadBytes))
  const now = UnixTime()

  if (exp && exp < now) return Failure("JWT token has expired")
  if (iat && iat > now + 60) return Failure("JWT token issued in the future")

  return Success(payload as T)
}
